import React, { useCallback, useEffect, useState } from 'react';
import { Slide, Snackbar } from '@material-ui/core';

interface Notification {
  id: number;
  body: React.ReactNode;
  vertical?: 'top' | 'bottom';
  horizontal?: 'left' | 'right';
  slideDirection?: 'left' | 'right' | 'up' | 'down';
}

let nextId = 1;
// Store an array of notification addition handlers.
// Each time Notifications component is mounted, it adds its `handleAddNotification` into the handler.
// So that when Notification.notify is invoked, all mounted Notifications component will be able to display the notification
const handlers: ((notification: Notification) => void)[] = [];

export const Notifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const handleAddNotification = useCallback(
    (notification: Notification) => {
      setNotifications([...notifications, notification]);
    },
    [notifications],
  );
  const handleRemoveNotification = useCallback(
    (notification: Notification) => {
      setNotifications(notifications.filter((n) => n.id !== notification.id));
    },
    [notifications],
  );

  useEffect(() => {
    handlers.push(handleAddNotification);
    return () => {
      const index = handlers.indexOf(handleAddNotification);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    };
  }, [handleAddNotification]);

  const renderNotification = (notification: Notification) => {
    return (
      <Snackbar
        anchorOrigin={{
          vertical: notification.vertical || 'bottom',
          horizontal: notification.horizontal || 'right',
        }}
        key={notification.id}
        open={true}
        autoHideDuration={3000}
        onClose={() => handleRemoveNotification(notification)}
        message={notification.body}
        TransitionComponent={(props) => (
          <Slide {...props} direction={notification.slideDirection || 'left'} />
        )}
      />
    );
  };

  return <div>{notifications.map((n) => renderNotification(n))}</div>;
};

Notifications.notify = (
  data: Pick<Notification, 'body' | 'vertical' | 'horizontal'>,
) => {
  const notification = {
    id: ++nextId,
    ...data,
  };
  for (const handler of handlers) {
    handler(notification);
  }
};
